/*
Copyright 2025 Element Creations Ltd.
Copyright 2025 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/
import {
  ConnectionState as LivekitConnectionState,
  type LocalAudioTrack,
  type LocalTrackPublication,
  LocalVideoTrack,
  ParticipantEvent,
  type Room as LivekitRoom,
  RoomEvent,
  Track,
} from "livekit-client";
import {
  combineLatest,
  distinctUntilChanged,
  map,
  NEVER,
  type Observable,
  type Subscription,
  switchMap,
} from "rxjs";
import { type Logger } from "matrix-js-sdk/lib/logger";

import type { Behavior } from "../../Behavior.ts";
import type { MediaDevices, SelectedDevice } from "../../MediaDevices.ts";
import type { MuteStates } from "../../MuteStates.ts";
import {
  type ProcessorState,
  trackProcessorSync,
} from "../../../livekit/TrackProcessorContext.tsx";
import { getUrlParams } from "../../../UrlParams.ts";
import { observeTrackReference$ } from "../../observeTrackReference";
import { type Connection } from "../remoteMembers/Connection.ts";
import { ObservableScope } from "../../ObservableScope.ts";
import {
  cameraCaptureStatus$,
  cameraPresetsEnabledForRoom,
  cameraPreferencesEqual,
  type CameraPreferences,
  clearActiveCameraCaptureStatus,
  getCameraPreferences,
  getCameraVideoOptions,
  reportCameraCaptureActive,
  reportCameraCaptureError,
} from "../../../livekit/cameraPreferences.ts";
import {
  cameraFrameRate,
  cameraResolution,
} from "../../../settings/settings.ts";

function errorFromUnknown(error: unknown): Error {
  if (error instanceof Error) return error;
  const result = new Error(String(error));
  if (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    typeof error.name === "string"
  ) {
    result.name = error.name;
  }
  return result;
}

/**
 * A wrapper for a Connection object.
 * This wrapper will manage the connection used to publish to the LiveKit room.
 * The Publisher is also responsible for creating the media tracks.
 */
export class Publisher {
  /**
   * By default, livekit will start publishing tracks as soon as they are created.
   * In the matrix RTC world, we want to control when tracks are published based
   * on whether the user is part of the RTC session or not.
   */
  public shouldPublish = false;

  private readonly scope = new ObservableScope();

  private readonly cameraPresetsEnabled: boolean;

  private cameraPreferenceUpdate = Promise.resolve();

  private audioInputRestart = Promise.resolve();

  private audioInputRestartPending = false;

  private appliedCameraPreferences = getCameraPreferences();

  private cameraPreferencesNeedApplication = false;

  private readonly cameraErrorRequests = new WeakMap<
    Error,
    CameraPreferences
  >();

  private destroyed = false;

  /**
   * Creates a new Publisher.
   * @param connection - The connection to use for publishing.
   * @param devices - The media devices to use for audio and video input.
   * @param muteStates - The mute states for audio and video.
   * @param trackerProcessorState$ - The processor state for the video track processor (e.g. background blur).
   * @param logger - The logger to use for logging :D.
   */
  public constructor(
    private connection: Pick<Connection, "livekitRoom" | "state$">, //setE2EEEnabled,
    private readonly devices: MediaDevices,
    private readonly muteStates: MuteStates,
    trackerProcessorState$: Behavior<ProcessorState>,
    private logger: Logger,
  ) {
    const { controlledAudioDevices } = getUrlParams();
    const room = connection.livekitRoom;
    this.cameraPresetsEnabled = cameraPresetsEnabledForRoom(room);
    if (!this.cameraPresetsEnabled) cameraCaptureStatus$.next(null);

    room.setE2EEEnabled(room.options.e2ee !== undefined)?.catch((e: Error) => {
      this.logger.error("Failed to set E2EE enabled on room", e);
    });

    // Setup track processor syncing (blur)
    this.observeTrackProcessors(this.scope, room, trackerProcessorState$);
    // Observe media device changes and update LiveKit active devices accordingly
    this.observeMediaDevices(this.scope, devices, controlledAudioDevices);
    // Keep capture constraints and sender encodings in sync with camera settings.
    this.observeCameraPreferences(this.scope, room, devices);

    this.workaroundRestartAudioInputTrackChrome(devices, this.scope);

    this.connection.livekitRoom.localParticipant.on(
      ParticipantEvent.LocalTrackPublished,
      this.onLocalTrackPublished.bind(this),
    );
    room.on(RoomEvent.MediaDevicesError, this.onMediaDevicesError);
  }

  public async destroy(): Promise<void> {
    this.destroyed = true;
    // Revoke publishing permission before waiting for any in-flight camera
    // work. A LiveKit restart may replace the sender track and resume it, so
    // the completion path must observe this state and pause it again before
    // destruction continues.
    this.shouldPublish = false;
    this.scope.end();
    this.connection.livekitRoom.off(
      RoomEvent.MediaDevicesError,
      this.onMediaDevicesError,
    );
    await this.audioInputRestart;
    await this.cameraPreferenceUpdate;
    this.logger.info("Scope ended -> unset handler");
    this.muteStates.audio.unsetHandler();
    this.muteStates.video.unsetHandler();

    this.logger.info(`Start to stop tracks`);
    try {
      await this.stopTracks();
      this.logger.info(`Done to stop tracks`);
    } catch (e) {
      this.logger.error(`Failed to stop tracks: ${e}`);
    }
  }

  // LiveKit will publish the tracks as soon as they are created
  // but we want to control when tracks are published.
  // We cannot just mute the tracks, even if this will effectively stop the publishing,
  // it would also prevent the user from seeing their own video/audio preview.
  // So for that we use pauseUpStream():  Stops sending media to the server by replacing
  // the sender track with null, but keeps the local MediaStreamTrack active.
  // The user can still see/hear themselves locally, but remote participants see nothing.
  private onLocalTrackPublished(
    localTrackPublication: LocalTrackPublication,
  ): void {
    if (this.destroyed) return;
    this.logger.info("Local track published", localTrackPublication);
    const lkRoom = this.connection.livekitRoom;
    if (!this.shouldPublish) {
      this.logger.debug("Not publishing, pausing upstream");
      this.pauseUpstreams(lkRoom, [localTrackPublication.source]).catch((e) => {
        this.logger.error(`Failed to pause upstreams`, e);
      });
    }
    if (localTrackPublication.source === Track.Source.Microphone) {
      if (
        this.audioInputRestartPending &&
        localTrackPublication.audioTrack !== undefined
      ) {
        void this.queueAudioInputRestart(localTrackPublication.audioTrack);
      }
      const muteState = this.muteStates.audio;
      // skip this if a sync is in progress: enabled$ still reflects the old
      // state while the handler is mid-flight, so the handler itself will apply
      // the correct mute state once it completes.
      if (!muteState.syncing$.value) {
        const enabled = muteState.enabled$.value;
        if (!enabled) {
          this.logger.info(
            "Local audio track just published but muted meanwhile, setting enabled to false",
          );
          lkRoom.localParticipant.setMicrophoneEnabled(false).catch((e) => {
            this.logger.error(
              `Failed to enable microphone track, enabled:${enabled}`,
              e,
            );
          });
        }
      }
    } else if (localTrackPublication.source === Track.Source.Camera) {
      const videoTrack = localTrackPublication.videoTrack;
      if (videoTrack && this.cameraPresetsEnabled) {
        const preferences = getCameraPreferences();
        reportCameraCaptureActive(videoTrack, preferences);
        if (
          !this.cameraPreferencesNeedApplication &&
          this.publicationUsesCameraPreferences(
            localTrackPublication,
            preferences,
          )
        ) {
          this.appliedCameraPreferences = preferences;
        } else {
          // Capture/publish may have started using defaults snapshotted before
          // a settings change. Bring the newly published track up to date.
          void this.queueCameraPreferenceUpdate(
            lkRoom,
            this.devices,
            preferences,
          );
        }
      }
      const muteState = this.muteStates.video;
      // skip this if a sync is in progress: enabled$ still reflects the old
      // state while the handler is mid-flight, so the handler itself will apply
      // the correct mute state once it completes.
      if (!muteState.syncing$.value) {
        const enabled = muteState.enabled$.value;
        if (!enabled) {
          this.logger.info(
            "Local video track just published but muted meanwhile, setting enabled to false",
          );
          lkRoom.localParticipant.setCameraEnabled(false).catch((e) => {
            this.logger.error(
              `Failed to enable camera track, enabled:${enabled}`,
              e,
            );
          });
        }
      }
    }
  }
  /**
   * Create and setup local audio and video tracks based on the current mute states.
   * It creates the tracks only if audio and/or video is enabled, to avoid unnecessary
   * permission prompts.
   *
   * It also observes mute state changes to update LiveKit microphone/camera states accordingly.
   * If a track is not created initially because disabled, it will be created when unmuting.
   *
   * This call is not blocking anymore, instead callers can listen to the
   * `RoomEvent.MediaDevicesError` event in the LiveKit room to be notified of any errors.
   *
   */
  public async createAndSetupTracks(): Promise<void> {
    this.logger.debug("createAndSetupTracks called");
    const lkRoom = this.connection.livekitRoom;
    // Observe mute state changes and update LiveKit microphone/camera states accordingly
    this.observeMuteStates();

    // Check if audio and/or video is enabled. We only create tracks if enabled,
    // because it could prompt for permission, and we don't want to do that unnecessarily.
    const audio = this.muteStates.audio.enabled$.value;
    const video = this.muteStates.video.enabled$.value;

    // We don't await the creation, because livekit could block until the tracks
    // are fully published, and not only that they are created.
    // We don't have control on that, localParticipant creates and publishes the tracks
    // asap.
    // We are using the `ParticipantEvent.LocalTrackPublished` to be notified
    // when tracks are actually published, and at that point
    // we can pause upstream if needed (depending on if startPublishing has been called).
    if (audio && video) {
      // Enable both at once in order to have a single permission prompt!
      void lkRoom.localParticipant.enableCameraAndMicrophone();
    } else if (audio) {
      void lkRoom.localParticipant.setMicrophoneEnabled(true);
    } else if (video) {
      void lkRoom.localParticipant.setCameraEnabled(true);
    }

    return Promise.resolve();
  }

  private async pauseUpstreams(
    lkRoom: LivekitRoom,
    sources: Track.Source[],
  ): Promise<void> {
    for (const source of sources) {
      const track = lkRoom.localParticipant.getTrackPublication(source)?.track;
      if (track) {
        await track.pauseUpstream();
      } else {
        this.logger.warn(
          `No track found for source ${source} to pause upstream`,
        );
      }
    }
  }

  private async resumeUpstreams(
    lkRoom: LivekitRoom,
    sources: Track.Source[],
  ): Promise<void> {
    for (const source of sources) {
      const track = lkRoom.localParticipant.getTrackPublication(source)?.track;
      if (track) {
        await track.resumeUpstream();
      } else {
        this.logger.warn(
          `No track found for source ${source} to resume upstream`,
        );
      }
    }
  }

  /**
   *
   * Request to publish local tracks to the LiveKit room.
   * This will wait for the connection to be ready before publishing.
   * Livekit also have some local retry logic for publishing tracks.
   * Can be called multiple times, localparticipant manages the state of published tracks (or pending publications).
   *
   * @returns
   */
  public async startPublishing(): Promise<void> {
    if (this.destroyed) {
      this.logger.debug(`Publisher destroyed, ignoring startPublishing call`);
      return;
    }
    if (this.shouldPublish) {
      this.logger.debug(`Already publishing, ignoring startPublishing call`);
      return;
    }
    this.shouldPublish = true;
    this.logger.debug("startPublishing called");

    const lkRoom = this.connection.livekitRoom;

    const selectedVideoDeviceId = this.devices.videoInput.selected$.value?.id;
    if (
      selectedVideoDeviceId !== undefined &&
      lkRoom.getActiveDevice("videoinput") !== selectedVideoDeviceId
    ) {
      await this.queueVideoDeviceSwitch(
        lkRoom,
        this.devices,
        selectedVideoDeviceId,
      );
    }

    if (this.destroyed || !this.shouldPublish) return;

    if (this.audioInputRestartPending) {
      const audioTrack = lkRoom.localParticipant.getTrackPublication(
        Track.Source.Microphone,
      )?.audioTrack;
      if (audioTrack) await this.queueAudioInputRestart(audioTrack);
    }

    if (this.destroyed || !this.shouldPublish) return;

    // Camera changes are deliberately deferred while publishing is not
    // allowed: LiveKit's restartTrack replaces the sender track and can resume
    // upstream. Apply the latest pending choice only after permission has been
    // granted, before the general resume below.
    if (this.cameraPreferencesNeedApplication) {
      await this.queueCameraPreferenceUpdate(
        lkRoom,
        this.devices,
        getCameraPreferences(),
      );
    }

    if (this.destroyed || !this.shouldPublish) return;

    // Resume upstream for both audio and video tracks
    // We need to call it explicitly because call setTrackEnabled does not always
    // resume upstream. It will only if you switch the track from disabled to enabled,
    // but if the track is already enabled but upstream is paused, it won't resume it.
    // TODO what about screen share?
    try {
      await this.resumeUpstreams(lkRoom, [
        Track.Source.Microphone,
        Track.Source.Camera,
      ]);
      // stopPublishing/destroy may have raced the asynchronous LiveKit resume.
      // Restore the privacy invariant if permission was revoked meanwhile.
      if (this.destroyed || !this.shouldPublish) {
        await this.pauseUpstreams(lkRoom, [
          Track.Source.Microphone,
          Track.Source.Camera,
        ]);
      }
    } catch (e) {
      this.logger.error(`Failed to resume upstreams`, e);
    }
  }

  public async stopPublishing(): Promise<void> {
    this.logger.debug("stopPublishing called");
    this.shouldPublish = false;
    // Pause upstream will stop sending media to the server, while keeping
    // the local MediaStreamTrack active, so the user can still see themselves.
    await this.pauseUpstreams(this.connection.livekitRoom, [
      Track.Source.Microphone,
      Track.Source.Camera,
      Track.Source.ScreenShare,
    ]);
  }

  public async stopTracks(): Promise<void> {
    const lkRoom = this.connection.livekitRoom;
    for (const source of [
      Track.Source.Microphone,
      Track.Source.Camera,
      Track.Source.ScreenShare,
    ]) {
      const localPub = lkRoom.localParticipant.getTrackPublication(source);
      if (localPub?.track) {
        // stops and unpublishes the track
        await lkRoom.localParticipant.unpublishTrack(localPub!.track, true);
      }
    }
  }

  /// Private methods

  // Restart the audio input track whenever we detect that the active media
  // device has changed to refer to a different hardware device. We do this
  // for the sake of Chrome, which provides a "default" device that is meant
  // to match the system's default audio input, whatever that may be.
  // This is special-cased for only audio inputs because we need to dig around
  // in the LocalParticipant object for the track object and there's not a nice
  // way to do that generically. There is usually no OS-level default video capture
  // device anyway, and audio outputs work differently.
  private workaroundRestartAudioInputTrackChrome(
    devices: MediaDevices,
    scope: ObservableScope,
  ): void {
    const lkRoom = this.connection.livekitRoom;
    devices.audioInput.selected$
      .pipe(
        switchMap((device) => device?.hardwareDeviceChange$ ?? NEVER),
        scope.bind(),
      )
      .subscribe(() => {
        if (lkRoom.state != LivekitConnectionState.Connected) return;
        const activeMicTrack = Array.from(
          lkRoom.localParticipant.audioTrackPublications.values(),
        ).find((d) => d.source === Track.Source.Microphone)?.track;

        if (
          activeMicTrack &&
          // only restart if the stream is still running: LiveKit will detect
          // when a track stops & restart appropriately, so this is not our job.
          // Plus, we need to avoid restarting again if the track is already in
          // the process of being restarted.
          activeMicTrack.mediaStreamTrack.readyState !== "ended"
        ) {
          this.audioInputRestartPending = true;
          void this.queueAudioInputRestart(activeMicTrack);
        }
      });
  }

  private async queueAudioInputRestart(
    track: Pick<LocalAudioTrack, "restartTrack" | "pauseUpstream">,
  ): Promise<void> {
    this.audioInputRestart = this.audioInputRestart.then(async () => {
      if (this.destroyed || !this.shouldPublish) {
        this.audioInputRestartPending = true;
        return;
      }

      this.audioInputRestartPending = false;
      this.logger.info(
        "Restarting audio device track due to active media device changed (workaroundRestartAudioInputTrackChrome)",
      );
      try {
        // Restart the track, which will cause LiveKit to do another
        // getUserMedia() call with deviceId: default to get the new default
        // device. room.switchActiveDevice() cannot do this because the id is
        // still `default`.
        await track.restartTrack();
      } catch (error) {
        this.logger.error(`Failed to restart audio device track`, error);
      } finally {
        // Like video restartTrack, audio replacement may resume its sender.
        // Restore the paused state if stop/destroy raced the restart.
        if (this.destroyed || !this.shouldPublish) {
          try {
            await track.pauseUpstream();
          } catch (error) {
            this.logger.error(
              "Failed to restore paused audio upstream after track restart",
              error,
            );
          }
        }
      }
    });
    await this.audioInputRestart;
  }

  // Observe changes in the selected media devices and update the LiveKit room accordingly.
  private observeMediaDevices(
    scope: ObservableScope,
    devices: MediaDevices,
    controlledAudioDevices: boolean,
  ): void {
    const lkRoom = this.connection.livekitRoom;
    const syncDevice = (
      kind: MediaDeviceKind,
      selected$: Observable<SelectedDevice | undefined>,
    ): Subscription =>
      selected$.pipe(scope.bind()).subscribe((device) => {
        if (lkRoom.state != LivekitConnectionState.Connected) return;
        // if (this.connectionState$.value !== ConnectionState.Connected) return;
        this.logger.info(
          "[LivekitRoom] syncDevice room.getActiveDevice(kind) !== d.id :",
          lkRoom.getActiveDevice(kind),
          " !== ",
          device?.id,
        );
        if (
          device !== undefined &&
          lkRoom.getActiveDevice(kind) !== device.id
        ) {
          lkRoom
            .switchActiveDevice(kind, device.id)
            .catch((e: Error) =>
              this.logger.error(
                `Failed to sync ${kind} device with LiveKit`,
                e,
              ),
            );
        }
      });

    syncDevice("audioinput", devices.audioInput.selected$);
    if (!controlledAudioDevices)
      syncDevice("audiooutput", devices.audioOutput.selected$);
    devices.videoInput.selected$.pipe(scope.bind()).subscribe((device) => {
      if (
        device !== undefined &&
        lkRoom.state === LivekitConnectionState.Connected &&
        lkRoom.getActiveDevice("videoinput") !== device.id
      ) {
        void this.queueVideoDeviceSwitch(lkRoom, devices, device.id);
      }
    });
  }

  private async queueVideoDeviceSwitch(
    room: LivekitRoom,
    devices: MediaDevices,
    deviceId: string,
    allowEnabledTransition = false,
  ): Promise<void> {
    this.cameraPreferenceUpdate = this.cameraPreferenceUpdate
      .then(async () => {
        const track = room.localParticipant.getTrackPublication(
          Track.Source.Camera,
        )?.videoTrack;
        if (
          this.destroyed ||
          !this.shouldPublish ||
          !track ||
          track.isMuted ||
          (!allowEnabledTransition && !this.muteStates.video.enabled$.value) ||
          devices.videoInput.selected$.value?.id !== deviceId
        ) {
          return;
        }
        if (room.getActiveDevice("videoinput") !== deviceId) {
          await room.switchActiveDevice("videoinput", deviceId);
        }
        if (
          this.destroyed ||
          !this.shouldPublish ||
          devices.videoInput.selected$.value?.id !== deviceId
        ) {
          if (this.destroyed || !this.shouldPublish) {
            const currentTrack = room.localParticipant.getTrackPublication(
              Track.Source.Camera,
            )?.videoTrack;
            if (currentTrack) await this.pauseCameraAfterRestart(currentTrack);
          }
          return;
        }

        if (!this.cameraPresetsEnabled) return;
        const preferences = getCameraPreferences();
        if (
          this.cameraPreferencesNeedApplication ||
          !cameraPreferencesEqual(preferences, this.appliedCameraPreferences)
        ) {
          // A previous camera may have rejected the saved selection. Try it
          // again after switching to a device that may support it.
          await this.applyCameraPreferences(
            room,
            devices,
            preferences,
            allowEnabledTransition,
          );
        } else {
          const track = room.localParticipant.getTrackPublication(
            Track.Source.Camera,
          )?.videoTrack;
          if (track && !track.isMuted && this.muteStates.video.enabled$.value) {
            reportCameraCaptureActive(track, preferences);
          }
        }
      })
      .catch(async (error) => {
        if (this.destroyed || !this.shouldPublish) {
          const track = room.localParticipant.getTrackPublication(
            Track.Source.Camera,
          )?.videoTrack;
          if (track) await this.pauseCameraAfterRestart(track);
        }
        this.logger.error("Failed to sync video input device", error);
      });
    await this.cameraPreferenceUpdate;
  }

  private readonly onMediaDevicesError = (
    error: Error,
    kind?: MediaDeviceKind,
  ): void => {
    if (!this.destroyed && this.cameraPresetsEnabled && kind === "videoinput") {
      const requested =
        this.cameraErrorRequests.get(error) ?? getCameraPreferences();
      this.cameraErrorRequests.delete(error);
      reportCameraCaptureError(error, requested);
    }
  };

  private publicationUsesCameraPreferences(
    publication: LocalTrackPublication,
    preferences: CameraPreferences,
  ): boolean {
    if (!this.cameraPresetsEnabled) return false;
    const expected = getCameraVideoOptions(preferences).publish.videoEncoding;
    const actual =
      publication.options?.videoEncoding ??
      publication.videoTrack?.publishOptions?.videoEncoding;
    return (
      actual?.maxBitrate === expected?.maxBitrate &&
      actual?.maxFramerate === expected?.maxFramerate
    );
  }

  private observeCameraPreferences(
    scope: ObservableScope,
    room: LivekitRoom,
    devices: MediaDevices,
  ): void {
    if (!this.cameraPresetsEnabled) return;
    combineLatest([cameraResolution.value$, cameraFrameRate.value$])
      .pipe(
        map(([resolution, frameRate]) => ({ resolution, frameRate })),
        distinctUntilChanged(cameraPreferencesEqual),
        scope.bind(),
      )
      .subscribe((preferences) => {
        const options = getCameraVideoOptions(preferences);
        room.options.videoCaptureDefaults = {
          ...room.options.videoCaptureDefaults,
          ...options.capture,
        };
        room.options.publishDefaults = {
          ...room.options.publishDefaults,
          ...options.publish,
        };

        this.cameraPreferencesNeedApplication = !cameraPreferencesEqual(
          preferences,
          this.appliedCameraPreferences,
        );

        if (!this.cameraPreferencesNeedApplication) {
          return;
        }

        // Serializing restarts avoids racing getUserMedia calls if a user changes
        // both controls quickly. Stale queued choices are skipped before capture.
        void this.queueCameraPreferenceUpdate(room, devices, preferences);
      });
  }

  private async queueCameraPreferenceUpdate(
    room: LivekitRoom,
    devices: MediaDevices,
    preferences: CameraPreferences,
    allowEnabledTransition = false,
  ): Promise<void> {
    if (!this.cameraPresetsEnabled) return;
    this.cameraPreferenceUpdate = this.cameraPreferenceUpdate
      .then(async () => {
        if (
          this.destroyed ||
          !cameraPreferencesEqual(preferences, getCameraPreferences()) ||
          (!this.cameraPreferencesNeedApplication &&
            cameraPreferencesEqual(preferences, this.appliedCameraPreferences))
        ) {
          return;
        }
        await this.applyCameraPreferences(
          room,
          devices,
          preferences,
          allowEnabledTransition,
        );
      })
      .catch((error) => {
        this.logger.error(
          "Unexpected failure while applying camera preferences",
          error,
        );
      });
    await this.cameraPreferenceUpdate;
  }

  private async applyCameraPreferences(
    room: LivekitRoom,
    devices: MediaDevices,
    requested: CameraPreferences,
    allowEnabledTransition = false,
  ): Promise<void> {
    const previous = this.appliedCameraPreferences;
    const publication = room.localParticipant.getTrackPublication(
      Track.Source.Camera,
    );
    const track = publication?.videoTrack;

    // Never restart while upstream publishing is disallowed. LiveKit may
    // internally resume the sender while replacing its track. The pending flag
    // is intentionally retained so startPublishing can apply the choice.
    // During a successful unmute the real MuteState does not emit enabled=true
    // until its handler returns, hence the explicit transition allowance.
    if (
      this.destroyed ||
      !this.shouldPublish ||
      !track ||
      track.isMuted ||
      (!allowEnabledTransition && !this.muteStates.video.enabled$.value)
    ) {
      return;
    }

    const selectedDeviceId = devices.videoInput.selected$.value?.id;
    const deviceId = selectedDeviceId ? { exact: selectedDeviceId } : undefined;
    const requestedOptions = getCameraVideoOptions(requested);
    const previousPublishOptions = track.publishOptions;
    const previousPublicationOptions = publication.options;
    track.publishOptions = {
      ...previousPublishOptions,
      ...requestedOptions.publish,
    };
    publication.options = {
      ...previousPublicationOptions,
      ...requestedOptions.publish,
    };
    // LiveKit normally refreshes sender encodings when capture dimensions
    // change. Clear its cached dimensions so a frame-rate-only change also
    // reapplies maxFramerate to the active sender.
    track.lastEncodedDimensions = undefined;

    try {
      await track.restartTrack({
        ...requestedOptions.capture,
        deviceId,
      });

      // A stop/destroy can race restartTrack. Since LiveKit's sender may have
      // been resumed by the completed replacement, re-pause before any early
      // return or cleanup.
      if (this.destroyed || !this.shouldPublish) {
        await this.pauseCameraAfterRestart(track);
      }
      if (this.destroyed) return;

      this.appliedCameraPreferences = requested;
      if (cameraPreferencesEqual(requested, getCameraPreferences())) {
        this.cameraPreferencesNeedApplication = false;
      }
      reportCameraCaptureActive(track, requested);
    } catch (error) {
      track.publishOptions = previousPublishOptions;
      publication.options = previousPublicationOptions;
      if (this.destroyed || !this.shouldPublish) {
        await this.pauseCameraAfterRestart(track);
        return;
      }
      const mediaError = errorFromUnknown(error);
      this.cameraErrorRequests.set(mediaError, requested);
      room.emit(RoomEvent.MediaDevicesError, mediaError, "videoinput");
      this.logger.error(
        "Camera does not support the requested capture preferences",
        error,
      );

      // Keep the call usable at the last known working capture settings. This
      // does not alter the persisted choice, which remains visible in settings.
      try {
        // Permission is checked immediately before the call, without an await
        // in between, so stop/destroy cannot interleave before restartTrack is
        // invoked. If either races the restart, the post-await check re-pauses.
        if (this.destroyed || !this.shouldPublish) return;
        await track.restartTrack({
          ...getCameraVideoOptions(previous).capture,
          deviceId,
        });
        if (this.destroyed || !this.shouldPublish) {
          await this.pauseCameraAfterRestart(track);
        }
      } catch (recoveryError) {
        this.logger.error(
          "Failed to restore the previous camera capture preferences",
          recoveryError,
        );
      }
    }
  }

  private async pauseCameraAfterRestart(track: LocalVideoTrack): Promise<void> {
    try {
      await track.pauseUpstream();
    } catch (error) {
      this.logger.error(
        "Failed to restore paused camera upstream after track restart",
        error,
      );
    }
  }

  /**
   * Observe changes in the mute states and update the LiveKit room accordingly.
   * @private
   */
  private observeMuteStates(): void {
    const lkRoom = this.connection.livekitRoom;
    this.muteStates.audio.setHandler(async (enable) => {
      try {
        this.logger.debug(
          `handler: Setting LiveKit microphone enabled: ${enable}`,
        );
        await lkRoom.localParticipant.setMicrophoneEnabled(enable);
        // Unmute will restart the track if it was paused upstream,
        // but until explicitly requested, we want to keep it paused.
        if (!this.shouldPublish && enable) {
          await this.pauseUpstreams(lkRoom, [Track.Source.Microphone]);
        }
        return enable;
      } catch (e) {
        this.logger.error("Failed to update LiveKit audio input mute state", e);
        return lkRoom.localParticipant.isMicrophoneEnabled;
      }
    });
    this.muteStates.video.setHandler(async (enable) => {
      try {
        this.logger.debug(`handler: Setting LiveKit camera enabled: ${enable}`);
        await lkRoom.localParticipant.setCameraEnabled(enable);
        if (!enable) clearActiveCameraCaptureStatus();
        if (enable) {
          // setCameraEnabled may resume an existing sender. Re-pause it before
          // any deferred work when this publisher is not yet allowed upstream.
          if (!this.shouldPublish) {
            await this.pauseUpstreams(lkRoom, [Track.Source.Camera]);
          }

          const selectedDeviceId = this.devices.videoInput.selected$.value?.id;
          if (
            selectedDeviceId !== undefined &&
            lkRoom.getActiveDevice("videoinput") !== selectedDeviceId
          ) {
            await this.queueVideoDeviceSwitch(
              lkRoom,
              this.devices,
              selectedDeviceId,
              true,
            );
          }

          const preferences = getCameraPreferences();
          const publication = lkRoom.localParticipant.getTrackPublication(
            Track.Source.Camera,
          );
          if (
            publication?.videoTrack &&
            this.publicationUsesCameraPreferences(publication, preferences) &&
            cameraPreferencesEqual(preferences, this.appliedCameraPreferences)
          ) {
            reportCameraCaptureActive(publication.videoTrack, preferences);
          } else {
            await this.queueCameraPreferenceUpdate(
              lkRoom,
              this.devices,
              preferences,
              true,
            );
          }
        }
        // Unmute will restart the track if it was paused upstream,
        // but until explicitly requested, we want to keep it paused.
        if ((this.destroyed || !this.shouldPublish) && enable) {
          await this.pauseUpstreams(lkRoom, [Track.Source.Camera]);
        }
        return enable;
      } catch (e) {
        this.logger.error("Failed to update LiveKit video input mute state", e);
        return lkRoom.localParticipant.isCameraEnabled;
      }
    });
  }

  private observeTrackProcessors(
    scope: ObservableScope,
    room: LivekitRoom,
    trackerProcessorState$: Behavior<ProcessorState>,
  ): void {
    const track$ = scope.behavior(
      observeTrackReference$(room.localParticipant, Track.Source.Camera).pipe(
        map((trackRef) => {
          const track = trackRef?.publication.track;
          return track instanceof LocalVideoTrack ? track : null;
        }),
      ),
      null,
    );
    trackProcessorSync(scope, track$, trackerProcessorState$);
  }
}
